import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CalendarEntry } from '../calendar-entry.js';
import { DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { ISyncEventWithGroup, NotificationEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { Duration } from 'dayjs/plugin/duration.js';
import { Cardinal } from '../core/cardinal.js';
import { VersionRange } from '../core/version-range.js';

export class CalendarEntryGroupCanceledEvent extends NotificationEvent implements ISyncEventWithGroup {
  static readonly TYPE_ID: string = '3c5deb9d65ca4d7e917bb932da3dc7f6';
  static readonly SCHEMA_VERSION: number = 2;

  constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string | null,
      public readonly deviceId: string | null,
      public readonly creationDate: dayjs.Dayjs,
      public readonly source: SyncEventSource,
      public readonly groupId: string,
      public readonly repeatCount: Cardinal,
      public readonly repeatInterval: Duration,
      public readonly seen = false,
      public readonly calendarEntry: CalendarEntry,
  ) {
    super();
  }

  get $typeId(): string {
    return CalendarEntryGroupCanceledEvent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CalendarEntryGroupCanceledEvent.SCHEMA_VERSION);
  }

  static fromMap(context: SerializationContext, map: SerializedData): CalendarEntryGroupCanceledEvent {
    return new CalendarEntryGroupCanceledEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource), SyncEventSource),
        MapUtils.get<string>(map, 'groupId'),
        Cardinal.decode(MapUtils.get<number>(map, 'repeatCount')),
        context.decodeDuration(MapUtils.get<number>(map, 'repeatInterval')),
        MapUtils.get<boolean>(map, 'seen'),
        context.fromData<CalendarEntry>(MapUtils.get<SerializedData>(map, 'calendarEntry')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'groupId': this.groupId,
      'repeatCount': this.repeatCount.encode(),
      'repeatInterval': context.encodeDuration(this.repeatInterval),
      'calendarEntry': context.toData(this.calendarEntry),
      'seen': this.seen,
    };
  }

  clone(): this {
    return new CalendarEntryGroupCanceledEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.groupId,
        this.repeatCount.clone(),
        this.repeatInterval,
        this.seen,
        this.calendarEntry.clone(),
    ) as this;
  }

  isSame(other: CalendarEntryGroupCanceledEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        other.groupId === this.groupId &&
        isSameData(other.repeatCount, this.repeatCount) &&
        other.repeatInterval === this.repeatInterval &&
        isSameData(other.calendarEntry, this.calendarEntry) &&
        other.seen === this.seen;
  }

  markAsSeen(): this {
    return new CalendarEntryGroupCanceledEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.groupId,
        this.repeatCount.clone(),
        this.repeatInterval,
        true,
        this.calendarEntry.clone(),
    ) as this;
  }
}

export class CalendarEntryGroupCanceledEventBuilder extends DataModelBuilder<CalendarEntryGroupCanceledEvent> {
  build(context: SerializationContext, map: SerializedData): CalendarEntryGroupCanceledEvent {
    return CalendarEntryGroupCanceledEvent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CalendarEntryGroupCanceledEvent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CalendarEntryGroupCanceledEvent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    /**
     * Version 2:
     *  - add `deviceId` of type: string | null
     */
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
